/**
 * NavigationButton Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 471-148
 *
 * A navigation button component with icon and text support.
 * Supports light/dark modes and interactive states.
 */

import React, { useState } from 'react';
import { GestureResponderEvent, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { Icon, type IconName } from '../icons';
import { colors } from '../tokens/colors';

export interface NavigationButtonProps {
  /**
   * Icon name to display
   */
  icon?: IconName;

  /**
   * Button text label
   */
  text?: string;

  /**
   * Whether to show the icon
   * @default true
   */
  showIcon?: boolean;

  /**
   * Whether to show the text
   * @default true
   */
  showText?: boolean;

  /**
   * Visual mode
   * @default 'light'
   */
  mode?: 'light' | 'dark';

  /**
   * Whether the button is in active state
   * @default false
   */
  active?: boolean;

  /**
   * Whether the button is in collapsed/icon-only mode
   * @default false
   */
  collapsed?: boolean;

  /**
   * Callback when button is pressed
   */
  onPress?: (e: GestureResponderEvent) => void;
}

export const NavigationButton: React.FC<NavigationButtonProps> = ({
  icon = 'Activity',
  text = 'Activity',
  showIcon = true,
  showText = true,
  mode = 'light',
  active = false,
  collapsed = false,
  onPress,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // Determine current state for styling
  const currentState = active ? 'active' : isPressed ? 'active' : isHovered ? 'hover' : 'default';

  // Get styles based on mode and state using Crop Circle color tokens
  const getBackgroundColor = () => {
    if (mode === 'dark') {
      switch (currentState) {
        case 'hover':
        case 'active':
          return colors.slate[100]; // #ECEEF1
        case 'default':
        default:
          return colors.slate[700]; // #262A33
      }
    } else { // light mode
      switch (currentState) {
        case 'active':
          return colors.neutral[700]; // #1A1A1A
        case 'hover':
          return colors.slate[200]; // #C8CDD5
        case 'default':
        default:
          return colors.slate[100]; // #ECEEF1
      }
    }
  };

  const getTextColor = () => {
    if (mode === 'dark') {
      switch (currentState) {
        case 'hover':
        case 'active':
          return colors.brand.black; // #020202
        case 'default':
        default:
          return colors.brand.white; // #FCFCFC
      }
    } else { // light mode
      switch (currentState) {
        case 'active':
          return colors.brand.white; // #FCFCFC
        case 'hover':
        case 'default':
        default:
          return colors.brand.black; // #020202
      }
    }
  };

  const backgroundColor = getBackgroundColor();
  const textColor = getTextColor();

  // When collapsed, show only the icon in a square button
  const shouldShowText = showText && !collapsed;

  return (
    <Pressable
      onPress={onPress}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={[
        styles.container,
        {
          backgroundColor,
          ...(isWeb ? {
            width: collapsed ? 32 : undefined,
            minWidth: collapsed ? 32 : 105,
            paddingHorizontal: collapsed ? 8 : 12,
          } : {
            height: 40,
            justifyContent: 'center',
          }),
        },
      ]}
    >
      {showIcon && icon && (
        <View style={styles.iconContainer}>
          <Icon name={icon} size={16} color={textColor} />
        </View>
      )}
      {shouldShowText && text && (
        <Text style={[styles.text, { color: textColor, ...(isWeb ? { opacity: collapsed ? 0 : 1 } : {}) }]}>
          {text}
        </Text>
      )}
    </Pressable>
  );
};

const isWeb = Platform.OS === 'web';

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    height: 32,
    borderRadius: 100,
    paddingHorizontal: isWeb ? 12 : 16,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: isWeb ? 'flex-start' : 'stretch',
    gap: 8,
    ...(isWeb ? {
      overflow: 'hidden',
      cursor: 'pointer',
      // @ts-ignore - web-specific transition
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    } : {}),
  },
  iconContainer: {
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  text: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    ...(isWeb ? {
      whiteSpace: 'nowrap',
      // @ts-ignore - web-specific transition
      transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    } : {}),
  },
});

NavigationButton.displayName = 'NavigationButton';
